import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCar, FaHome, FaCheckCircle, FaCalendarAlt, FaStar, FaMapMarkerAlt, FaShieldAlt, FaLeaf, FaArrowRight, FaClock } from 'react-icons/fa';
import './LocalPage.css';

const BANNER_IMG = "https://images.unsplash.com/photo-1520340356584-f9917d1eea6f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80";

const LocalPage = () => {
    const { city } = useParams();

    // Format city name: laval-auto-paris -> Paris
    const cityName = city
        .replace(/-/g, ' ')
        .replace(/\b\w/g, (l) => l.toUpperCase());

    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = `Nettoyage Auto à Domicile ${cityName} | RoulezPropre`;
    }, [cityName]);

    return (
        <div className="local-page-wrapper">
            {/* Banner Section */}
            <section className="standard-banner-section">
                <div className="standard-banner-bg">
                    <img src={BANNER_IMG} alt={`Nettoyage auto ${cityName}`} />
                    <div className="standard-banner-overlay"></div>
                </div>
                <div className="container standard-banner-content">
                    <div className="standard-title-box">
                        <span className="standard-banner-label">SERVICES À DOMICILE</span>
                        <h1>NETTOYAGE AUTO <span className="text-gradient-blue">{cityName.toUpperCase()}</span></h1>
                    </div>
                </div>
            </section>

            {/* Intro / SEO Context */}
            <section className="local-intro-section">
                <div className="container">
                    <div className="local-intro-grid">
                        <motion.div
                            className="local-intro-text"
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                        >
                            <span className="premium-tag">PROXIMITÉ & QUALITÉ</span>
                            <h2>Votre expert en esthétique automobile à <span className="text-blue">{cityName}</span></h2>
                            <p>
                                Vous habitez à <strong>{cityName}</strong> ou dans ses environs ?
                                <strong> RoulezPropre</strong> se déplace directement à votre domicile ou sur votre lieu de travail
                                pour redonner à votre véhicule son éclat d'origine.
                            </p>
                            <p>
                                Plus besoin de vous déplacer en station de lavage ou d'attendre votre tour.
                                Nous apportons tout le matériel nécessaire (vapeur haute pression, produits éco-responsables)
                                pour un résultat professionnel sans que vous ayez à bouger de chez vous.
                            </p>
                            <div className="local-features-mini">
                                <div className="mini-feat">
                                    <FaCheckCircle className="icon" />
                                    <span>Déplacement Gratuit</span>
                                </div>
                                <div className="mini-feat">
                                    <FaCheckCircle className="icon" />
                                    <span>Résultat Garanti</span>
                                </div>
                                <div className="mini-feat">
                                    <FaCheckCircle className="icon" />
                                    <span>Produits Bio</span>
                                </div>
                            </div>
                        </motion.div>
                        <motion.div
                            className="local-intro-image"
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                        >
                            <div className="image-frame">
                                <img src="https://images.unsplash.com/photo-1601362840469-51e4d8d59db2?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" alt="Lavage auto vapeur" />
                                <div className="experience-badge">
                                    <strong>+1000</strong>
                                    <span>Clients Satisfaits</span>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Services Grid */}
            <section className="local-services-section">
                <div className="container">
                    <div className="section-header-centered">
                        <h2>Nos Prestations à {cityName}</h2>
                        <div className="underline"></div>
                    </div>
                    <div className="local-services-grid">
                        <div className="local-service-card">
                            <div className="icon-box"><FaCar /></div>
                            <h3>Nettoyage Intérieur</h3>
                            <p>Aspiration profonde, nettoyage des plastiques, shampouinage des sièges et désinfection vapeur.</p>
                            <Link to="/nettoyage-voiture" className="link">Découvrir les tarifs <FaArrowRight /></Link>
                        </div>
                        <div className="local-service-card">
                            <div className="icon-box"><FaShieldAlt /></div>
                            <h3>Nettoyage Extérieur</h3>
                            <p>Lavage haute performance, décontamination de la carrosserie et protection longue durée.</p>
                            <Link to="/nettoyage-voiture" className="link">En savoir plus <FaArrowRight /></Link>
                        </div>
                        <div className="local-service-card">
                            <div className="icon-box"><FaHome /></div>
                            <h3>Nettoyage Mobilier</h3>
                            <p>Canapés, tapis, matelas... Nous intervenons aussi à l'intérieur de votre maison à {cityName}.</p>
                            <Link to="/nettoyage-mobiliers" className="link">Voir les services <FaArrowRight /></Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Local Stats / Why Us */}
            <section className="local-why-us">
                <div className="container">
                    <div className="why-us-box">
                        <div className="why-item">
                            <FaMapMarkerAlt className="icon" />
                            <h4>Zone {cityName}</h4>
                            <p>Intervention rapide dans tous les quartiers de {cityName}.</p>
                        </div>
                        <div className="why-item">
                            <FaClock className="icon" />
                            <h4>Gain de temps</h4>
                            <p>On s'occupe de tout pendant que vous êtes chez vous ou au travail.</p>
                        </div>
                        <div className="why-item">
                            <FaLeaf className="icon" />
                            <h4>Eco-Responsable</h4>
                            <p>On consomme 90% d'eau en moins qu'un lavage classique.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="local-cta-section">
                <div className="container">
                    <div className="cta-glass-card">
                        <h2>Prêt à retrouver un véhicule comme neuf ?</h2>
                        <p>Réservez votre créneau à <strong>{cityName}</strong> en quelques clics.</p>
                        <div className="cta-buttons">
                            <Link to="/reservation" className="btn-primary-premium">RÉSERVER MAINTENANT</Link>
                            <Link to="/contact" className="btn-secondary-premium">DEMANDER UN DEVIS</Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default LocalPage;
